/* eslint-disable react/no-unescaped-entities */
/**
 * /operator-claim/:token — landing page for fishing-tour operators
 * after they tap the "Claim this listing" CTA in their lead email.
 *
 * Mirrors /venue-signup/:token in spirit but tuned for tour operators:
 *   - Shows the listing as it currently appears on Hustle Directory
 *   - Asks for owner contact + ABN + (optional) website
 *   - On accept, status flips to ``claimed`` and future leads go to
 *     the verified owner email.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, ShieldCheck, MapPin, Fish, Anchor } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

export default function OperatorClaimPage() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [abn, setAbn] = useState("");
  const [website, setWebsite] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    api.get(`/api/fishing-tours/claim/${token}`)
      .then(r => {
        setData(r.data);
        if (r.data.status === "claimed") setPhase("done");
        else {
          setWebsite(r.data.tour?.website || "");
          setPhase("ready");
        }
      })
      .catch(e => {
        setError(e?.response?.data?.detail || "Invalid claim link");
        setPhase("error");
      });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setPhase("submitting"); setError("");
    try {
      const r = await api.post(`/api/fishing-tours/claim/${token}/accept`, {
        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim(),
        owner_phone: ownerPhone || null,
        abn: abn || null,
        website: website || null,
        confirm_owner: confirmed,
      });
      setData(d => ({ ...d, ...r.data }));
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
      setPhase("ready");
    }
  };

  if (phase === "loading") {
    return <div style={s.root}><div style={s.loading}><Loader2 className="spin" /> Loading…</div><style>{spin}</style></div>;
  }
  if (phase === "error") {
    return (
      <div style={s.root}>
        <div style={s.card} data-testid="claim-error">
          <h2 style={s.h2}>That link didn't work</h2>
          <p style={s.sub}>{error}</p>
        </div>
      </div>
    );
  }
  if (phase === "done") {
    return (
      <div style={s.root}>
        <div style={s.card} data-testid="claim-done">
          <div style={s.doneBadge}><CheckCircle2 size={40} /></div>
          <h2 style={s.h2}>You're verified ✓</h2>
          <p style={s.sub}>
            {data.tour?.name || data.tour_name} is now a claimed listing on
            Hustle Directory.  All future enquiries will be sent straight
            to <strong>{ownerEmail || data.owner_email}</strong>.
          </p>
          <p style={s.sub}>
            Want featured placement, photo uploads or a calendar widget?
            <br/>
            <a href="/sales" style={{ color: "#0ea5e9" }}>
              Explore the operator pricing →
            </a>
          </p>
        </div>
        <style>{spin}</style>
      </div>
    );
  }

  const tour = data.tour || {};
  return (
    <div style={s.root} data-testid="claim-root">
      <header style={s.header}>
        <div style={s.brand}>Hustle<span style={{ color: "#fbbf24" }}>Directory</span></div>
        <div style={s.brandSub}>Operator claim</div>
      </header>

      <div style={s.card}>
        <h1 style={s.h1}>Claim {tour.name}</h1>
        <p style={s.sub}>
          60-second verification. Once you claim this listing, all future
          fishing-tour enquiries from Hustle Directory land straight in
          your inbox at the email you give us — no more relying on
          forwards from a generic Hustle alias.
        </p>

        {/* Listing preview */}
        <section style={s.preview} data-testid="claim-listing-preview">
          {tour.image && <div style={{ ...s.previewImg,
                                       backgroundImage: `url("${tour.image}")` }} />}
          <div style={{ padding: 18 }}>
            <div style={s.previewName}>{tour.name}</div>
            <div style={s.previewMeta}>
              <MapPin size={11} /> {tour.region}, {tour.country}
              {tour.vessel && <> <span style={{ color: "#cbd5e1" }}>•</span>
                <Anchor size={10} /> {tour.vessel}</>}
            </div>
            {tour.target_species && (
              <div style={s.previewSpecies}>
                <Fish size={11} /> {tour.target_species}
              </div>
            )}
            <p style={s.previewDesc}>{tour.description}</p>
          </div>
        </section>

        <div style={s.badge}>
          <ShieldCheck size={14} /> Why we're asking
        </div>
        <p style={s.body}>
          We need to verify you're the legitimate operator before flipping
          this listing to a claimed status. We'll match your business name
          and email against our records and send a confirmation to the
          email below.
        </p>

        <form onSubmit={submit} style={s.form} data-testid="claim-form">
          <Field label="Your name">
            <input required value={ownerName}
                   onChange={e => setOwnerName(e.target.value)}
                   style={s.input} data-testid="claim-name" />
          </Field>
          <Field label="Email (where future leads will go)">
            <input required type="email" value={ownerEmail}
                   onChange={e => setOwnerEmail(e.target.value)}
                   style={s.input} data-testid="claim-email" />
          </Field>
          <div style={s.row2}>
            <Field label="Phone">
              <input type="tel" value={ownerPhone}
                     onChange={e => setOwnerPhone(e.target.value)}
                     style={s.input} data-testid="claim-phone" />
            </Field>
            <Field label="ABN / business reg #">
              <input value={abn} onChange={e => setAbn(e.target.value)}
                     style={s.input} data-testid="claim-abn" />
            </Field>
          </div>
          <Field label="Website">
            <input type="url" value={website}
                   onChange={e => setWebsite(e.target.value)}
                   style={s.input} data-testid="claim-website" />
          </Field>
          <label style={s.checkbox} data-testid="claim-confirm-wrap">
            <input type="checkbox" checked={confirmed}
                   onChange={e => setConfirmed(e.target.checked)}
                   data-testid="claim-confirm" />
            <span>
              I am the legitimate owner / authorised representative of
              <strong> {tour.name}</strong> and I want to claim this
              Hustle Directory listing.
            </span>
          </label>
          {error && <div style={s.err}>{error}</div>}
          <button type="submit"
                  disabled={!confirmed || phase === "submitting"}
                  style={{ ...s.submitBtn,
                           opacity: (!confirmed || phase === "submitting") ? 0.5 : 1 }}
                  data-testid="claim-submit">
            {phase === "submitting"
              ? <><Loader2 size={16} className="spin" /> Verifying…</>
              : <><CheckCircle2 size={16} /> Claim my listing</>}
          </button>
        </form>
      </div>
      <style>{spin}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={s.field}>
      <span style={s.label}>{label}</span>
      {children}
    </label>
  );
}

const spin = `.spin{animation:spin 1s linear infinite}
              @keyframes spin{to{transform:rotate(360deg)}}`;

const s = {
  root: { background: "#f5f1e8", minHeight: "100vh", padding: "24px 16px",
          fontFamily: "Inter, system-ui, sans-serif", color: "#051824" },
  header: { maxWidth: 720, margin: "0 auto 24px", display: "flex",
            justifyContent: "space-between", alignItems: "baseline" },
  brand: { fontFamily: "Georgia, serif", fontSize: 24, fontWeight: 800,
           letterSpacing: "-0.02em" },
  brandSub: { fontSize: 12, color: "#64748b", letterSpacing: "0.1em",
              textTransform: "uppercase" },
  card: { maxWidth: 720, margin: "0 auto", background: "white",
          borderRadius: 16, padding: 36,
          boxShadow: "0 20px 60px rgba(0,0,0,0.06)" },
  loading: { padding: 80, textAlign: "center", color: "#475569" },
  h1: { fontFamily: "Georgia, serif", fontSize: 32, fontWeight: 800,
        margin: "0 0 8px", letterSpacing: "-0.025em" },
  h2: { fontSize: 24, fontWeight: 800, margin: "0 0 8px",
        letterSpacing: "-0.02em" },
  sub: { color: "#475569", fontSize: 14, lineHeight: 1.55,
         margin: "0 0 24px" },
  preview: { background: "#f8fafc", border: "1px solid #e2e8f0",
             borderRadius: 12, overflow: "hidden", margin: "24px 0 28px" },
  previewImg: { aspectRatio: "21/9", backgroundSize: "cover",
                backgroundPosition: "center",
                background: "linear-gradient(135deg, #1aa3c7 0%, #051824 70%)",
                backgroundBlendMode: "overlay" },
  previewName: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  previewMeta: { fontSize: 12, color: "#475569",
                 display: "flex", alignItems: "center", gap: 5,
                 marginBottom: 6 },
  previewSpecies: { fontSize: 12, color: "#1aa3c7", fontWeight: 600,
                    marginBottom: 10, display: "flex",
                    alignItems: "center", gap: 5 },
  previewDesc: { fontSize: 13, color: "#475569", lineHeight: 1.55,
                 margin: 0 },
  badge: { display: "inline-flex", alignItems: "center", gap: 6,
           background: "#0f172a", color: "#fbbf24",
           padding: "5px 12px", borderRadius: 999, fontSize: 11,
           letterSpacing: "0.1em", textTransform: "uppercase",
           fontWeight: 700, marginBottom: 10 },
  body: { color: "#1f2937", lineHeight: 1.55, fontSize: 13,
          margin: "0 0 24px" },
  form: { display: "flex", flexDirection: "column", gap: 4 },
  field: { display: "flex", flexDirection: "column", gap: 4,
           marginBottom: 12 },
  label: { fontSize: 11, fontWeight: 700, color: "#64748b",
           textTransform: "uppercase", letterSpacing: "0.06em" },
  input: { padding: "10px 12px", border: "1px solid #e2e8f0",
           borderRadius: 8, fontSize: 14, outline: "none",
           fontFamily: "inherit" },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 },
  checkbox: { display: "flex", alignItems: "start", gap: 10,
              marginTop: 12, padding: 14, background: "#f8fafc",
              border: "1px solid #e2e8f0", borderRadius: 10,
              fontSize: 13, lineHeight: 1.5, cursor: "pointer" },
  submitBtn: { marginTop: 16, padding: "14px 18px", background: "#051824",
               color: "white", border: "none", borderRadius: 10,
               fontWeight: 700, fontSize: 15, cursor: "pointer",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", gap: 8 },
  err: { background: "#fee2e2", color: "#991b1b", padding: "10px 14px",
         borderRadius: 8, fontSize: 13, marginTop: 12 },
  doneBadge: { width: 64, height: 64, borderRadius: "50%",
               background: "#dcfce7", color: "#16a34a",
               display: "inline-flex", alignItems: "center",
               justifyContent: "center", marginBottom: 16 },
};
